import React from 'react';

import { createRoot } from 'react-dom/client';

import { closeButtonIcon } from '../../img/icons';

import renderBooks from '../Books/Books';

import useMediaQuery from '../../hooks/useMediaQuery';

import { trapFocus, removeTrapFocus, getFocusableElements } from '../utils/component-utils';

// Store the last opener button globally within this module
let lastOpenerButton = null;

const removeDrawers = ( gridID ) => {
  document
    .querySelectorAll( `.${gridID} .team-member` )
    .forEach( function( teamMember ) {
      teamMember.classList.remove( 'selected' );
    });
  const drawer = document.querySelector( `#drawer-${gridID}` );
  if ( drawer ) {
    removeTrapFocus( drawer );
    drawer.remove();
    // Restore focus to the opener button if available
    if ( lastOpenerButton ) {
      lastOpenerButton.focus();
      lastOpenerButton = null;
    }
  }
};

const determineDrawerLocation = ( teamMember, gridID ) => {
  const screenWidth = document.body.clientWidth;
  const totalTeamMembers = window.DP[gridID].length;
  const index = parseInt( teamMember.dataset.index );

  let rowLength;
  if ( screenWidth > 1024 ) {
    rowLength = 4;
  } else if ( screenWidth > 768 ) {
    rowLength = 3;
  } else {
    rowLength = 2;
  }

  let lastRowIndex, arrowClass;
  if ( index % rowLength === 0 ) { // first item
    lastRowIndex = index + rowLength - 1;
    arrowClass = 'first';
  } else if ( index % rowLength === 1 ) { // second item
    lastRowIndex = index + rowLength - 2;
    arrowClass = 'second';
  } else if ( index % rowLength === 2 ) { // third item
    lastRowIndex = index + rowLength - 3;
    arrowClass = 'third';
  } else if ( index % rowLength === 3 ) { // fourth item
    lastRowIndex = index;
    arrowClass = 'fourth';
  }
  
  if ( lastRowIndex >= totalTeamMembers ) { // in case of an uneven last row, set it to the last element
    lastRowIndex = totalTeamMembers - 1;
  }

  return { lastRowIndex, arrowClass };
};

const TeamMemberDrawer = ({ index, gridID }) => {
  const isMobile = useMediaQuery( '( max-width: 768px )' );
  const teamMember = window.DP[gridID][index];
  const paddingBottomClass = !teamMember.rendered_carousel ? 'pb-40' : '';

  const scrollToTop = () => {
    const drawer = document.querySelector( `#drawer-${gridID}` );
    if ( drawer ) {
      isMobile
        ? drawer.scrollIntoView({ block: 'start' })
        : drawer.scrollIntoView({ block: 'center' });
    }
  };

  React.useEffect( () => {
    const drawer = document.querySelector( `#drawer-${gridID}` );
    if ( !drawer ) return;

    // Helper to focus and trap after carousel is loaded
    const focusAndTrap = () => {
      // Move focus to the close button or first focusable element
      const focusableEls = getFocusableElements( drawer );
      if ( focusableEls.length ) focusableEls[0].focus();
      trapFocus( drawer );
    };

    // If carousel is present, wait for it to be rendered
    if ( teamMember.rendered_carousel ) {
      const observer = new MutationObserver( () => {
        const carousel = drawer.querySelector( '#carousel' );
        if ( carousel && carousel.children.length > 0 ) {
          observer.disconnect();
          focusAndTrap();
        }
      });
      observer.observe( drawer, { childList: true, subtree: true });
      // Fallback: if carousel is already present
      setTimeout( () => {
        const carousel = drawer.querySelector( '#carousel' );
        if ( carousel && carousel.children.length > 0 ) {
          observer.disconnect();
          focusAndTrap();
        }
      }, 500 );
      // Clean up
      return () => {
        observer.disconnect();
        removeTrapFocus( drawer );
      };
    } else {
      // No carousel, focus and trap immediately
      focusAndTrap();
      return () => removeTrapFocus( drawer );
    }
  }, [ gridID, teamMember.rendered_carousel ] );

  setTimeout( () => {
    scrollToTop();
  }, 100 );

  return (
    <>
      <div className="arrow m-auto max-w-lg"></div>
      <div className="mx-[5%] max-w-full">
        <button
          className="close-button ml-auto mr-0 block cursor-pointer p-10 text-2xl font-bold"
          onClick={() => removeDrawers( gridID )}
          aria-label="Close team member details"
        >
          {closeButtonIcon}
        </button>
      </div>
      <div
        className={`mx-auto px-20 xxs:max-w-[320px] xs:max-w-[375px] sm:max-w-sm sm:px-32 md:max-w-md lg:max-w-lg lg:px-64 xl:px-0 ${paddingBottomClass}`}
      >
        <div className="justify-center">
          <h3 className="heading-3 mb-0">
            {teamMember.acf.team_member_name}
          </h3>
          <p className="mt-10">{teamMember.acf.job_title}</p>
          <div
            className="lg:max-w-[75%]"
            dangerouslySetInnerHTML={{
              __html: teamMember.acf.team_member_bio
            }}
          ></div>
        </div>
      </div>
      <div>
        {!!teamMember.rendered_carousel && (
          <div
            id="carousel"
            dangerouslySetInnerHTML={{
              __html: teamMember.rendered_carousel
            }}
          ></div>
        )}
      </div>
    </>
  );
};

const addTeamMemberGridManager = () => {
  const teamMemberGrids = document.querySelectorAll(
    '[class^="team-member-grid team_grid_"]'
  );

  teamMemberGrids.forEach( ( teamMemberGrid ) => {
    teamMemberGrid
      .querySelectorAll( '.team-member' )
      .forEach( ( teamMember ) => {
        teamMember.addEventListener( 'click', function() {
          // Store the button that opened the drawer
          lastOpenerButton = this.querySelector( '.team-member-text' );
          const gridID = this.getAttribute( 'data-grid-id' ); // get specific grid id from attribute
          removeDrawers( gridID );

          // Create Drawer and insert after the teamMember
          const drawer = document.createElement( 'div' );
          drawer.id = 'drawer-' + gridID;
          drawer.dataset.gridId = gridID; // for focus trap cleanup
          drawer.classList.add( 'py-15' );
          drawer.classList.add( 'my-15' );
          const root = createRoot( drawer );

          root.render(
            <TeamMemberDrawer
              index={teamMember.dataset.index}
              gridID={gridID}
            />
          );

          teamMember.classList.add( 'selected' );

          const { lastRowIndex, arrowClass } =
                        determineDrawerLocation( teamMember, gridID );
          drawer.classList.add( arrowClass );

          const rootElement = teamMemberGrid.querySelector(
            `.item-${lastRowIndex}`
          );
          rootElement.parentNode.insertBefore(
            drawer,
            rootElement.nextSibling
          );

          setTimeout( () => {
            renderBooks();
          }, 500 );
        });
      });
  });
};

export default addTeamMemberGridManager;
